import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  InputAdornment,
  Typography,
  FormHelperText,
  FormControl,
  Checkbox,
} from '@material-ui/core';
import { Field, useFormikContext } from 'formik';
import { GridColumns, GridCellParams } from '@material-ui/data-grid';
import { TextField } from 'formik-material-ui';

import PaginatedDataGrid from 'components/PaginatedDataGrid';
import CollapsibleList from 'components/CollapsibleList';
import { EPSSelectionFormContext } from './interface';
import { REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { RootState } from 'store';

import { useStyles } from './styles';
import { IMailingRuleSubformProps } from '../../interfaces';

import { loadEpsListAction } from 'modules/epsList';

const Form: React.FC<IMailingRuleSubformProps> = ({
  isEditable,
}: IMailingRuleSubformProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    values,
    errors,
    touched,
    setFieldValue,
  } = useFormikContext<EPSSelectionFormContext>();

  useEffect(() => {
    if (values.serviceCategory) {
      dispatch(
        loadEpsListAction({
          EPSServicesCategory: values.serviceCategory,
          isActive: true,
          limit: 9999,
          offset: 0,
        }),
      );
    }
  }, [values.serviceCategory, dispatch, setFieldValue]);

  const { list, loading } = useSelector((state: RootState) => state.epsList);

  const handleSelectionChange = (rowId: string, companyName: string) => {
    const newSelectedEPS = Object.assign({}, values.selectedEPS);
    if (newSelectedEPS[rowId]) {
      delete newSelectedEPS[rowId];
    } else {
      newSelectedEPS[rowId] = {
        companyName,
        distributionPercentage: 100,
        remunerationType: '',
        remunerationBonusId: '',
        remunerationCommissionId: '',
        remunerationFixedIds: [],
        remunerationGoalIds: [],
        epsId: rowId,
      };
    }

    setFieldValue(`selectedEPS`, newSelectedEPS, true);
  };

  const formControlCheckbox = (params: GridCellParams) => (
    <FormControl>
      <Checkbox
        checked={!!values.selectedEPS[params.row.id]}
        onClick={() =>
          handleSelectionChange(
            params.row.id.toString(),
            params.row.companyName,
          )
        }
        id={params.row.id.toString()}
        disabled={!isEditable}
      />
    </FormControl>
  );

  const columns: GridColumns = [
    {
      field: '',
      width: 60,
      renderCell: (params) => formControlCheckbox(params),
    },
    {
      field: 'distribution',
      headerName: 'Distribuição',
      width: 150,
      renderCell: (params) =>
        values.selectedEPS[params.row.id] ? (
          <FormControl>
            <Field
              component={TextField}
              name={`selectedEPS.${params.row.id}.distributionPercentage`}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                error: typeof errors.selectedEPS === 'string',
              }}
              disabled={!isEditable}
            />
          </FormControl>
        ) : (
          <div />
        ),
    },
    { field: 'fantasyName', headerName: 'EPS', width: 300 },
    { field: '_id', headerName: 'Código', width: 220 },
    {
      field: 'EPSServicesType',
      headerName: 'Tipo de Serviço',
      width: 200,
      renderCell: (params) => (
        <CollapsibleList
          items={
            params.value as {
              [key: string]: string;
            }[]
          }
          mainItemId={values.serviceCategory}
          idPropName="_id"
          valuePropName="description"
        />
      ),
    },
    {
      field: 'EPSServicesCategory',
      headerName: 'Tipo de Atuação',
      width: 250,
      renderCell: (params) => (
        <CollapsibleList
          items={
            params.value as {
              [key: string]: string;
            }[]
          }
          idPropName="_id"
          valuePropName="description"
        />
      ),
    },
  ];

  return (
    <Grid
      data-cy="eps-selectiom-form__container--eps-selection-distribution"
      container
      direction="column"
    >
      <Grid item xs={12}>
        <Typography>Seleção de EPS para o segmento selecionado</Typography>
      </Grid>
      <Grid className="formik-error-selectedEPS_Root" item xs={12}>
        {typeof errors.selectedEPS_Root === 'string' && touched.selectedEPS && (
          <FormHelperText error>{errors.selectedEPS_Root}</FormHelperText>
        )}
      </Grid>
      <Grid
        data-cy="eps-selectiom-form__grid--eps-selection-distribution"
        item
        xs={12}
      >
        <div className={classes.datagrid}>
          <PaginatedDataGrid
            columns={columns}
            rows={list}
            pageSize={20}
            rowsPerPageOptions={[20]}
            loading={loading === REQUEST_LIFE_CICLE.PENDING}
            disableSelectionOnClick
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default Form;
